import { CircleAlert, Link2 } from "lucide-react";
import { OtaKey, OtaUrls } from "../../types/ota";
import { isTTravelHostname } from "../../utils/otaDomains";

export type OtaValues = Record<OtaKey, string>;

type OtaField = {
  key: OtaKey;
  label: string;
  example: string;
};

export const OTA_FIELDS: OtaField[] = [
  { key: "official_website", label: "Сайт", example: "https://brsochi.ru" },
  {
    key: "ostrovok",
    label: "Островок",
    example: "https://ostrovok.ru/hotel/*",
  },
  {
    key: "t_travel",
    label: "Т-Путешествия",
    example: "https://t-travel.ru/travel/*",
  },
  {
    key: "yandex_travel",
    label: "Яндекс Путешествия",
    example: "https://travel.yandex.ru/hotels/*",
  },
  {
    key: "sutochno",
    label: "Суточно",
    example: "https://sutochno.ru/*",
  },
  {
    key: "alean",
    label: "Alean",
    example: "https://alean.ru/*",
  },
  // {
  //   key: "booking",
  //   label: "Booking",
  //   example: "https://www.booking.com/hotel/*",
  // },
  // {
  //   key: "ozon",
  //   label: "Ozon",
  //   example: "https://www.ozon.ru/travel/*",
  // },
];

export const EMPTY_OTA_VALUES: OtaValues = {
  official_website: "",
  ostrovok: "",
  t_travel: "",
  yandex_travel: "",
  sutochno: "",
  alean: "",
  booking: "",
  ozon: "",
};

function parseHttpUrl(value: string) {
  if (!/^https?:\/\//i.test(value)) return false;
  try {
    const parsed = new URL(value);
    if (parsed.protocol !== "http:" && parsed.protocol !== "https:") return false;
    return parsed;
  } catch {
    return false;
  }
}

function isValidOtaUrl(value: string, field: OtaField) {
  const trimmed = value.trim();
  if (!trimmed) return true;
  const parsed = parseHttpUrl(trimmed);
  if (!parsed) return false;

  const normalizedUrl = trimmed.toLowerCase();

  switch (field.key) {
    case "official_website":
      return true;
    case "ostrovok":
      return normalizedUrl.includes("ostrovok.ru");
    case "t_travel":
      return isTTravelHostname(parsed.hostname);
    case "yandex_travel":
      return normalizedUrl.includes("travel.yandex.ru");
    case "alean":
      return normalizedUrl.includes("alean.ru");
    case "sutochno":
      return normalizedUrl.includes("sutochno.ru");
    case "booking":
      return /(^|\.)booking\.com$/i.test(parsed.hostname) && /^\/hotel(?:\/|$)/.test(parsed.pathname);
    case "ozon":
      return /(^|\.)ozon\.ru$/i.test(parsed.hostname) && /^\/travel(?:\/|$)/.test(parsed.pathname);
    default:
      return false;
  }
}

export function stripQuery(value: string) {
  return value.trim().split("?")[0];
}

export function normalizeOtaValues(
  urls?: Partial<Record<string, string | null>> | null,
  fallbackOfficialUrl?: string | null,
): OtaValues {
  const values = { ...EMPTY_OTA_VALUES };

  OTA_FIELDS.forEach((field) => {
    const value = urls?.[field.key];
    values[field.key] = typeof value === "string" ? value : "";
  });

  if (!values.official_website && fallbackOfficialUrl) {
    values.official_website = fallbackOfficialUrl;
  }

  return values;
}

export function getInvalidOtaKeys(values: OtaValues) {
  return new Set(
    OTA_FIELDS
      .filter((field) => !isValidOtaUrl(values[field.key], field))
      .map((field) => field.key),
  );
}

export function countFilledOtaUrls(values: OtaValues) {
  return OTA_FIELDS.filter((field) => values[field.key].trim()).length;
}

export function buildOtaUrlsPayload(values: OtaValues): OtaUrls {
  return OTA_FIELDS.reduce<OtaUrls>((acc, field) => {
    const value = stripQuery(values[field.key]);
    if (value) acc[field.key] = value;
    return acc;
  }, {});
}

type Props = {
  values: OtaValues;
  onChange: (key: OtaKey, value: string) => void;
  disabled?: boolean;
  officialLabel: string;
  title?: string;
};

export default function OtaUrlsFields({
  values,
  onChange,
  disabled = false,
  officialLabel,
  title = "Ссылки ОТА",
}: Props) {
  const invalidFields = getInvalidOtaKeys(values);
  const baseInputClass =
    "w-full rounded-2xl border bg-white/80 px-4 py-[13px] text-[15px] text-slate-900 placeholder:text-slate-400 shadow-inner transition focus:ring-4 disabled:opacity-60 disabled:cursor-not-allowed";

  return (
    <div className="space-y-4">
      <div>
        <h4 className="text-sm font-semibold uppercase tracking-wide text-slate-500">{title}</h4>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        {OTA_FIELDS.map((field) => {
          const invalid = invalidFields.has(field.key);
          const label = field.key === "official_website" ? officialLabel : field.label;

          return (
            <label key={field.key} className="block space-y-2">
              <span className="text-sm font-semibold text-slate-600">{label}</span>
              <div className="relative">
                <Link2 size={18} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  value={values[field.key]}
                  onChange={(event) => onChange(field.key, event.target.value)}
                  className={[
                    baseInputClass,
                    "pl-11 pr-11",
                    invalid
                      ? "border-rose-300 text-rose-900 focus:border-rose-400 focus:ring-rose-100"
                      : "border-slate-200/80 focus:border-indigo-300 focus:ring-indigo-200/60",
                  ].join(" ")}
                  placeholder="https://..."
                  disabled={disabled}
                />
                {invalid ? (
                  <span
                    className="group absolute right-4 top-1/2 inline-flex -translate-y-1/2 text-rose-500"
                    tabIndex={0}
                  >
                    <CircleAlert size={18} aria-label="Неверная ссылка" />
                    <span className="pointer-events-none absolute bottom-full right-0 z-20 mb-2 hidden w-64 rounded-xl border border-rose-100 bg-white px-3 py-2 text-xs font-medium leading-snug text-rose-700 shadow-[0_12px_30px_rgba(15,23,42,0.16)] group-hover:block group-focus:block">
                      Вставлена неверная ссылка, ссылка должна соответствовать примеру
                    </span>
                  </span>
                ) : null}
              </div>
              <span className="block break-all text-xs text-slate-400">Пример ссылки: {field.example}</span>
            </label>
          );
        })}
      </div>
    </div>
  );
}
